/**
 * Secure error handling utility that prevents sensitive information exposure in production
 */

import logger from './logger';

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Sanitizes error messages for production to prevent information disclosure
 * @param {Error|string} error - The error to sanitize
 * @param {string} fallbackMessage - Default message to show in production
 * @returns {string} - Safe error message
 */
export const sanitizeError = (error, fallbackMessage = 'An error occurred. Please try again.') => {
  if (isDevelopment) {
    // In development, show full error details
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.message) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return fallbackMessage;
  }
  
  // In production, only show safe, generic messages
  if (error?.response?.status === 401) {
    return 'Authentication required. Please log in.';
  }
  if (error?.response?.status === 403) {
    return 'Access denied. You do not have permission to perform this action.';
  }
  if (error?.response?.status === 404) {
    return 'The requested resource was not found.';
  }
  if (error?.response?.status >= 500) {
    return 'A server error occurred. Please try again later.';
  }
  if (error?.name === 'NetworkError' || error?.code === 'NETWORK_ERROR') {
    return 'Network connection error. Please check your connection and try again.';
  }
  
  return fallbackMessage;
};

/**
 * Sanitizes API error responses for safe display to users
 * @param {Object} error - Axios error object
 * @returns {Object} - Sanitized error object
 */
export const sanitizeApiError = (error) => {
  const sanitizedError = {
    message: sanitizeError(error),
    status: error?.response?.status || null,
    timestamp: new Date().toISOString()
  };
  
  // Only include detailed error information in development
  if (isDevelopment) {
    sanitizedError.details = {
      originalMessage: error?.message,
      responseData: error?.response?.data,
      stack: error?.stack
    };
  }
  
  return sanitizedError;
};

/**
 * Logs errors securely without exposing sensitive information
 * @param {Error} error - The error to log
 * @param {string} context - Context where the error occurred
 */
export const logError = (error, context = 'Unknown') => {
  const errorInfo = {
    context,
    message: error?.message || 'Unknown error',
    timestamp: new Date().toISOString()
  };
  
  if (isDevelopment) {
    logger.error(`[${context}] Error:`, error);
  } else {
    // In production, log minimal information
    logger.error(`[${context}] Error: ${errorInfo.message}`);
  }
};

/**
 * Creates a user-friendly error message based on the error type
 * @param {Error} error - The error object
 * @returns {string} - User-friendly error message
 */
export const getUserFriendlyError = (error) => {
  if (error?.response?.status === 401) {
    return 'Please log in to continue.';
  }
  if (error?.response?.status === 403) {
    return 'You do not have permission to perform this action.';
  }
  if (error?.response?.status === 404) {
    return 'The requested information was not found.';
  }
  if (error?.response?.status >= 500) {
    return 'We are experiencing technical difficulties. Please try again later.';
  }
  if (error?.name === 'NetworkError') {
    return 'Unable to connect to the server. Please check your internet connection.';
  }
  
  return 'Something went wrong. Please try again.';
};
